import React from 'react';
import { Link } from 'react-router-dom';

const LAST_UPDATED = '29 September 2025'; // update when needed

const TermsOfService: React.FC = () => (
  <main className="container">
    {/* Top mini-header with Home link */}
    <header style={{ paddingTop: '0', paddingBottom: '0.5rem' }}>
      <Link to="/" className="back-home-link" aria-label="Back to home">← Back to home</Link>
    </header>

    <article className="section" style={{ background: '#fff' }}>
      <h1>Terms of Service</h1>
      <p><em>Last updated: {LAST_UPDATED}</em></p>

      <p>
        Welcome to Sparki. These Terms of Service (“Terms”) govern your use of the Sparki website,
        mobile applications, and related services (together, the “Service”). These Terms apply to both
        garages (“Garages”) and end-users/customers (“Customers”). By accessing or using Sparki, you
        agree to these Terms. If you do not agree, do not use the Service.
      </p>

      <h2>1. About Sparki</h2>
      <p>
        Sparki provides digital tools to connect Garages and Customers for bookings, communication,
        and service management. Sparki is a technology platform and does not itself provide vehicle
        servicing or repairs. Contracts for those services are strictly between Customers and Garages.
      </p>

      <h2>2. Eligibility and Accounts</h2>
      <ul>
        <li>You must be at least 18 and capable of entering into binding agreements.</li>
        <li>You must provide accurate, complete information when creating an account.</li>
        <li>Keep your login credentials secure; you’re responsible for all activity under your account.</li>
        <li>We may suspend or close accounts that breach these Terms or pose risk.</li>
      </ul>

      <h2>3. Customer Responsibilities</h2>
      <ul>
        <li>Provide accurate booking details (e.g. vehicle registration, contact information).</li>
        <li>Attend confirmed appointments or follow applicable cancellation processes.</li>
        <li>Communicate respectfully with Garages via the platform.</li>
        <li>Understand Sparki does not guarantee the quality or outcome of Garage services.</li>
        <li>You are responsible for verifying the identity, credentials, and suitability of any Garage before agreeing to services or making payment.</li>
      </ul>

      <h2>4. Garage Responsibilities</h2>
      <ul>
        <li>Provide accurate, up-to-date information on services, pricing, and availability.</li>
        <li>Honour confirmed bookings unless prevented by events beyond reasonable control.</li>
        <li>Comply with applicable laws, regulations, licences, and industry standards.</li>
        <li>Ensure staff accessing Sparki are authorised and trained.</li>
        <li>Recognise you are an independent business, not an employee, agent, or partner of Sparki.</li>
        <li>You are solely responsible for fulfilling all agreed services, handling customer payments, and resolving any disputes with Customers.</li>
      </ul>

      <h2>5. Platform Role and Disclaimer</h2>
      <ul>
        <li>Sparki is a neutral technology platform that facilitates connections between Customers and Garages.</li>
        <li>Sparki is not a party to any agreement, contract, or transaction between Customers and Garages.</li>
        <li>Garages are independent businesses and are not employees, agents, partners, or representatives of Sparki.</li>
        <li>Sparki does not vet, guarantee, endorse, or verify any Garage, service, pricing, or outcome.</li>
        <li>All services are provided solely by the Garage at the Customer’s own risk.</li>
        <li>Sparki is not responsible or liable for any loss, damage, injury, delay, misrepresentation, fraud, or poor workmanship arising from Garage services.</li>
        <li>Any disputes must be resolved directly between the Customer and the Garage.</li>
        <li>Customers and Garages transact directly with each other. Sparki does not handle, hold, or guarantee any payments between users.</li>
        <li>Any payments made outside of Sparki (including in person, over the phone, or via third-party methods) are solely at the risk of the parties involved, and Sparki shall not be liable for any loss arising from such payments.</li>
        <li>Sparki is not responsible for any fraudulent activity, misrepresentation, impersonation, or failure by a Garage to perform agreed services.</li>
        <li>Sparki does not guarantee the identity, credentials, licensing, or legitimacy of any Garage or Customer.</li>
      </ul>

      <h2>6. Service Availability and Continuity</h2>
      <ul>
        <li>We aim to provide reliable access but the Service may be unavailable due to maintenance, updates, or technical issues.</li>
        <li>Features may be added, changed, or removed at any time.</li>
        <li>
          While we intend to continue supporting Sparki, we do not guarantee indefinite operation.
          Businesses should not rely on Sparki as their sole system and should maintain appropriate backups.
        </li>
      </ul>

      <h2>7. No Warranty</h2>
      <p>
        The Service is provided on an “as is” and “as available” basis. To the fullest extent permitted by law,
        Sparki makes no warranties, representations, or guarantees of any kind, whether express or implied,
        including but not limited to fitness for a particular purpose, accuracy, reliability, availability,
        or that the Service will be uninterrupted, secure, or error-free.
      </p>

      <h2>8. Payments and Fees (Future Provision)</h2>
      <ul>
        <li>Sparki does not currently process or facilitate payments between Customers and Garages.</li>
        <li>All payments are made directly between Customers and Garages at their own risk.</li>
        <li>Sparki is not responsible for any loss, fraud, chargebacks, or disputes arising from payments.</li>
        <li>If payment processing or platform fees are introduced in the future, separate terms will apply and users will be notified in advance.</li>
      </ul>

      <h2>9. Intellectual Property</h2>
      <ul>
        <li>All software, content, and branding of Sparki are owned by or licensed to us.</li>
        <li>You may not copy, modify, distribute, or create derivative works without written permission.</li>
        <li>Garages retain ownership of their business content uploaded to Sparki but grant us a licence to host, display, and use it in connection with the Service.</li>
        <li>Feedback or suggestions may be used by us without restriction or obligation.</li>
      </ul>

      <h2>10. Acceptable Use</h2>
      <ul>
        <li>Do not use the Service for unlawful, fraudulent, harmful, or abusive purposes.</li>
        <li>Do not interfere with or attempt unauthorised access to the Service or other users’ data.</li>
        <li>Do not post false, misleading, defamatory, infringing, or offensive content.</li>
        <li>We may suspend or terminate accounts that violate these rules.</li>
      </ul>

      <h2>11. Limitation of Liability</h2>
      <p>
        To the fullest extent permitted by law, Sparki shall not be liable for any indirect, incidental,
        special, consequential, or punitive damages, including but not limited to loss of profits, revenue,
        business, data, goodwill, or reputation, arising out of or in connection with use of the Service.
        Sparki shall not be liable for any loss resulting from:
        (a) reliance on the Service,
        (b) Service downtime, bugs, or errors,
        (c) actions or omissions of Garages or Customers,
        (d) failed, missed, or incorrect bookings,
        (e) unauthorised access or security breaches.
        Sparki’s total aggregate liability for any claim shall not exceed the amount paid by you (if any)
        to use the Service in the 12 months preceding the claim. Nothing excludes liability that cannot
        be excluded under applicable law.
      </p>

      <h2>User Responsibility and Risk</h2>
      <p>
        You acknowledge and agree that:
        (a) you use the Service entirely at your own risk,
        (b) you are responsible for verifying all booking details, services, counterparties, and payment arrangements,
        (c) Sparki does not guarantee the identity, legitimacy, or reliability of any user,
        (d) any payments made are entirely at your own risk and outside Sparki’s responsibility,
        (e) you are solely responsible for any decisions or actions taken based on information obtained through the Service.
      </p>

      <h2>12. Indemnity</h2>
      <p>
        You agree to indemnify and hold harmless Sparki, its owners, and affiliates from claims,
        damages, liabilities, costs, and expenses (including reasonable legal fees) arising from your
        use of the Service, your content, or your breach of these Terms.
      </p>

      <h2>No Reliance</h2>
      <p>
        You acknowledge that you have not relied on any statement, representation, or warranty made by Sparki
        other than those expressly set out in these Terms.
      </p>

      <h2>13. Privacy and Data Protection</h2>
      <p>
        Your use of Sparki is also governed by our Privacy Policy, which explains how we collect, use,
        share, and protect personal data under UK GDPR.
      </p>

      <h2>14. Termination</h2>
      <ul>
        <li>You may close your account at any time.</li>
        <li>We may suspend or terminate your account for breach of these Terms or where necessary to protect users or the Service.</li>
        <li>Provisions that by their nature should survive termination (e.g. IP, liability, governing law) will survive.</li>
      </ul>

      <h2>15. Force Majeure</h2>
      <p>
        We are not responsible for delays or failures caused by events beyond our reasonable control,
        including natural disasters, strikes, internet or hosting outages, acts of government, or
        other force majeure events.
      </p>

      <h2>16. Changes to These Terms</h2>
      <p>
        We may update these Terms from time to time. If we make material changes, we will notify you
        by posting the updated version here or via the Service. Continued use after changes take effect
        means you accept the updated Terms.
      </p>

      <h2>Third-Party Services</h2>
      <p>
        The Service may integrate with or rely on third-party services (including hosting, notifications,
        and payment providers). Sparki is not responsible for the availability, performance, or actions of
        such third parties.
      </p>

      <h2>17. Governing Law and Jurisdiction</h2>
      <p>
        These Terms are governed by the laws of England and Wales. Courts of England and Wales have
        exclusive jurisdiction over any dispute.
      </p>

      <h2>18. Contact Us</h2>
      <p>Email: <a className="footer-link" href="mailto:support@sparkiapp.com">support@sparkiapp.com</a></p>
    </article>
  </main>
);

export default TermsOfService;
